<script setup lang="ts">
import { ref, reactive } from "vue";
import { Button } from "./ui/button";
import { Card, CardHeader, CardContent, CardFooter } from "./ui/card";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { AlertCircle, Building2, Phone, Mail, Clock } from "lucide-vue-next";

interface ContactFormeProps {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const contactForm = reactive<ContactFormeProps>({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "产品咨询",
  message: "",
});

const invalidInputForm = ref<boolean>(false);
const phoneError = ref<string>("");

// 中国手机号正则表达式：1开头，第二位是3-9，总共11位数字
const phoneRegex = /^1[3-9]\d{9}$/;

const validatePhone = (phone: string): boolean => {
  if (!phone) {
    phoneError.value = "请输入手机号码";
    return false;
  }
  if (!phoneRegex.test(phone)) {
    phoneError.value = "请输入正确的手机号码格式";
    return false;
  }
  phoneError.value = "";
  return true;
};

const validateForm = (): boolean => {
  const { firstName, lastName, phone, subject } = contactForm;
  
  // 检查必填字段：姓氏、名字、手机号、咨询主题
  if (!firstName || !lastName || !phone || !subject) {
    invalidInputForm.value = true;
    return false;
  }
  
  if (!validatePhone(phone)) {
    invalidInputForm.value = true;
    return false;
  }
  
  invalidInputForm.value = false;
  return true;
};

const handleSubmit = () => {
  if (!validateForm()) {
    return;
  }
  
  const { firstName, lastName, email, phone, subject, message } = contactForm;
  console.log(contactForm);

  const mailToLink = `mailto:924121070@qq.com?subject=${subject}&body=您好，我是${firstName}${lastName}，我的邮箱是${email}，手机号是${phone}。%0D%0A${message}`;

  window.location.href = mailToLink;
};
</script>

<template>
  <section
    id="contact"
    class="container py-24 sm:py-32"
  >
    <section class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <div class="mb-4">
          <h2 class="text-lg text-primary mb-2 tracking-wider">联系我们</h2>

          <h2 class="text-3xl md:text-4xl font-bold">
            与我们取得
            <span class="text-transparent bg-gradient-to-r from-[#D247BF] to-primary bg-clip-text">
              联系
            </span>
          </h2>
        </div>
        <p class="mb-8 text-muted-foreground lg:w-5/6">
          欢迎联系创诚人工智能科技，我们将为您提供专业的物联网+AI解决方案咨询服务，助力您的企业数字化转型。
        </p>

        <div class="flex flex-col gap-4">
          <div>
            <div class="flex gap-2 mb-1">
              <Building2 />
              <div class="font-bold">公司地址</div>
            </div>

            <div>深圳市龙华区汇德大厦12楼</div>
          </div>

          <div>
            <div class="flex gap-2 mb-1">
              <Phone />
              <div class="font-bold">联系电话</div>
            </div>

            <div>13360971050</div>
          </div>

          <div>
            <div class="flex gap-2 mb-1">
              <Mail />
              <div class="font-bold">邮箱联系</div>
            </div>

            <div>924121070@qq.com</div>
          </div>

          <div>
            <div class="flex gap-2">
              <Clock />
              <div class="font-bold">工作时间</div>
            </div>

            <div>
              <div>周一至周五</div>
              <div>上午8:30 - 下午6:00</div>
            </div>
          </div>
        </div>
      </div>

      <!-- form -->
      <Card class="bg-muted/60 dark:bg-card">
        <CardHeader class="text-primary text-2xl"> </CardHeader>
        <CardContent>
          <form
            @submit.prevent="handleSubmit"
            class="grid gap-4"
          >
            <div class="flex flex-col md:flex-row gap-8">
              <div class="flex flex-col w-full gap-1.5">
                <Label for="first-name">姓氏 <span class="text-red-500">*</span></Label>
                <Input
                  id="first-name"
                  type="text"
                  placeholder="张"
                  v-model="contactForm.firstName"
                  required
                />
              </div>

              <div class="flex flex-col w-full gap-1.5">
                <Label for="last-name">名字 <span class="text-red-500">*</span></Label>
                <Input
                  id="last-name"
                  type="text"
                  placeholder="三"
                  v-model="contactForm.lastName"
                  required
                />
              </div>
            </div>

            <div class="flex flex-col gap-1.5">
              <Label for="phone">联系电话 <span class="text-red-500">*</span></Label>
              <Input
                id="phone"
                type="tel"
                placeholder="13800138000"
                v-model="contactForm.phone"
                @blur="validatePhone(contactForm.phone)"
                :class="{ 'border-red-500': phoneError }"
                required
              />
              <span v-if="phoneError" class="text-sm text-red-500">{{ phoneError }}</span>
            </div>

            <div class="flex flex-col gap-1.5">
              <Label for="subject">咨询主题 <span class="text-red-500">*</span></Label>

              <Select v-model="contactForm.subject" required>
                <SelectTrigger>
                  <SelectValue placeholder="请选择咨询主题" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="产品咨询">
                      产品咨询
                    </SelectItem>
                    <SelectItem value="技术支持">
                      技术支持
                    </SelectItem>
                    <SelectItem value="商务合作">商务合作</SelectItem>
                    <SelectItem value="解决方案">解决方案</SelectItem>
                    <SelectItem value="价格咨询">
                      价格咨询
                    </SelectItem>
                    <SelectItem value="其他">
                      其他
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div class="flex flex-col gap-1.5">
              <Label for="email">邮箱地址</Label>
              <Input
                id="email"
                type="email"
                placeholder="974121070@qq.com"
                v-model="contactForm.email"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <Label for="message">详细描述</Label>
              <Textarea
                id="message"
                placeholder="请详细描述您的需求或问题..."
                rows="5"
                v-model="contactForm.message"
              />
            </div>

            <Alert
              v-if="invalidInputForm"
              variant="destructive"
            >
              <AlertCircle class="w-4 h-4" />
              <AlertTitle>表单错误</AlertTitle>
              <AlertDescription>
                请填写所有必填项（带 * 号的字段）并确保信息格式正确。
              </AlertDescription>
            </Alert>

            <Button class="mt-4">发送消息</Button>
          </form>
        </CardContent>

        <CardFooter></CardFooter>
      </Card>
    </section>
  </section>
</template>

